import React from 'react';
import { UI_CAPTION_DF, UI_CAPTION_BOLD_DF } from 'oolib';
import { ContentBlock, TooltipHeadingProps, TooltipLink } from '../types';

const defaultContainerStyle: React.CSSProperties = {
    padding: '0.8rem',
    maxWidth: '30rem',
};

// Simple link renderer (without OKELink dependency)
const renderLink = (link: TooltipLink, linkStyle?: React.CSSProperties) => {
    return (
        <a
            href={link.href}
            target={link.newTab ? '_blank' : undefined}
            rel={link.newTab ? 'noopener noreferrer' : undefined}
            style={{
                color: 'inherit',
                textDecoration: 'underline',
                ...linkStyle,
            }}
        >
            {link.text}
        </a>
    );
};

const renderBlock = (
    block: ContentBlock,
    index: number,
    headingStyle?: React.CSSProperties,
    textStyle?: React.CSSProperties,
    linkStyle?: React.CSSProperties
) => {
    const { heading, text, link } = block;
    const linkElement = link ? renderLink(link, linkStyle) : null;
    const position = link?.position || 'end';

    const renderLine = (
        content: string | undefined,
        showLink: boolean,
        TextComponent: React.ComponentType<{ style?: React.CSSProperties; children: React.ReactNode }>,
        style?: React.CSSProperties
    ) => {
        if (!content && !showLink) return null;
        if (!content) return <TextComponent style={style}>{linkElement}</TextComponent>;
        return (
            <TextComponent style={style}>
                {position === 'start' && showLink && <>{linkElement} </>}
                {content}
                {position === 'end' && showLink && linkElement && <> {linkElement}</>}
            </TextComponent>
        );
    };

    return (
        <div key={index} style={{ marginTop: index > 0 ? '0.6rem' : 0 }}>
            {heading && renderLine(heading, !text, UI_CAPTION_BOLD_DF, headingStyle)}
            {(text || (!heading && link)) && renderLine(text, true, UI_CAPTION_DF, textStyle)}
        </div>
    );
};

export default function TooltipHeading({
    heading,
    text,
    blocks,
    containerStyle,
    headingStyle,
    textStyle,
    linkStyle,
}: TooltipHeadingProps) {
    // If blocks are provided, use the new multi-block rendering
    if (blocks && blocks.length > 0) {
        return (
            <div style={{ ...defaultContainerStyle, ...containerStyle }}>
                {blocks.map((block, index) =>
                    renderBlock(block, index, headingStyle, textStyle, linkStyle)
                )}
            </div>
        );
    }

    return (
        <div style={{ ...defaultContainerStyle, ...containerStyle }}>
            {heading && (
                <UI_CAPTION_BOLD_DF style={headingStyle}>
                    {heading}
                </UI_CAPTION_BOLD_DF>
            )}
            {text && (
                <UI_CAPTION_DF style={textStyle}>
                    {text}
                </UI_CAPTION_DF>
            )}
        </div>
    );
}
